import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { LargeInput, Button, TermsAndPrivacyDisclaimer } from "studio-design-system";
import { useAllAuthFunctions } from "~/hooks/auth/useAuth";
import { useAllAuthModalFunctions } from "~/hooks/auth/useAuthModal";
import { emailValidRegex } from "~/util/validateEmail";
import * as lightStyles from "./SocialSignUpForm.css";
import * as darkStyles from "../DarkModeStyles.css";
import { DarkInput } from "../DarkInput";
import Link from 'next/link';

interface IFormInput {
    email: string;
}

interface Props {
    onCreateAccount: () => void;
    onLogin: () => void;
    useDarkMode?: boolean;
}

export const SocialSignUpForm: React.FC<Props> = ({ onCreateAccount, onLogin, useDarkMode = false }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [appleLoading, setAppleLoading] = useState(false);
    const [submitErrorMessage, setSubmitErrorMessage] = useState<string | undefined>();
    const { signInWithGoogle, signInWithApple } = useAllAuthFunctions();
    const { closeAuthModal } = useAllAuthModalFunctions();

    // Use dark or light styles based on the useDarkMode prop
    const styles = useDarkMode ? darkStyles : lightStyles;

    const {
        control,
        handleSubmit,
        formState: { errors }
    } = useForm<IFormInput>();

    const onSubmit = async (data: IFormInput) => {
        setIsLoading(true);
        setSubmitErrorMessage(undefined);
        try {
            // Here we would proceed with email sign up
            onCreateAccount();
        } catch (error) {
            setSubmitErrorMessage(error instanceof Error ? error.message : "An error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setGoogleLoading(true);
        setSubmitErrorMessage(undefined);
        try {
            const result = await signInWithGoogle();
            if (result.type === "error") {
                setSubmitErrorMessage(result.error.message);
            } else {
                closeAuthModal();
            }
        } catch (error) {
            setSubmitErrorMessage(error instanceof Error ? error.message : "An error occurred");
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleAppleSignIn = async () => {
        setAppleLoading(true);
        setSubmitErrorMessage(undefined);
        try {
            const result = await signInWithApple();
            if (result.type === "error") {
                setSubmitErrorMessage(result.error.message);
            } else {
                closeAuthModal();
            }
        } catch (error) {
            setSubmitErrorMessage(error instanceof Error ? error.message : "An error occurred");
        } finally {
            setAppleLoading(false);
        }
    };

    return (
        <div className={styles.formContainer}>
            <button className={styles.closeButton} onClick={closeAuthModal} aria-label="Close">
                ✕
            </button>

            <div className={styles.title}>Sign up to continue</div>

            <button
                className={styles.socialButton}
                onClick={handleGoogleSignIn}
                disabled={googleLoading || appleLoading || isLoading}
            >
                {googleLoading ? (
                    <span>Loading...</span>
                ) : (
                    <>
                        <svg className={styles.socialButtonIcon} width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20455Z" fill="#4285F4" />
                            <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853" />
                            <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957273C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z" fill="#FBBC05" />
                            <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335" />
                        </svg>
                        Continue with Google
                    </>
                )}
            </button>

            <button
                className={styles.socialButton}
                onClick={handleAppleSignIn}
                disabled={googleLoading || appleLoading || isLoading}
            >
                {appleLoading ? (
                    <span>Loading...</span>
                ) : (
                    <>
                        <svg className={styles.socialButtonIcon} width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14.3246 11.6271C14.3419 9.91459 15.256 8.45604 16.6873 7.55604C15.7905 6.25984 14.3938 5.52334 12.65 5.3995C11.0032 5.27984 9.21914 6.37875 8.48255 6.37875C7.72051 6.37875 6.17096 5.42625 4.85505 5.42625C2.52051 5.46166 0 7.36166 0 11.2167C0 12.3654 0.196337 13.547 0.589013 14.7616C1.11173 16.3667 3.11801 20.2718 5.2246 20.2071C6.29641 20.1775 7.02428 19.5001 8.42573 19.5001C9.78301 19.5001 10.4591 20.2071 11.6455 20.2071C13.7695 20.1721 15.5941 16.6111 16.0923 14.9996C13.353 13.5621 14.3246 11.6686 14.3246 11.6271ZM11.7323 3.60395C12.9186 2.18416 12.7796 0.885834 12.7451 0.399795C11.6628 0.452543 10.4418 1.11516 9.77187 1.92875C9.04401 2.79771 8.72528 3.83245 8.7943 4.82416C9.97187 4.90147 10.8168 4.66147 11.7323 3.60395Z" fill={useDarkMode ? "#fff" : "black"} />
                        </svg>
                        Continue with Apple
                    </>
                )}
            </button>

            <div className={styles.divider}>
                <div className={styles.dividerLine}></div>
                <span className={styles.dividerText}>or</span>
                <div className={styles.dividerLine}></div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <Controller
                    control={control}
                    name="email"
                    rules={{
                        pattern: {
                            value: emailValidRegex,
                            message: "Please enter a valid email address",
                        },
                        required: { value: true, message: "You must provide an email." },
                    }}
                    render={({ field: { onChange, value, ref } }) => (
                        useDarkMode ? (
                            <DarkInput
                                ref={ref}
                                label="Email"
                                type="email"
                                errorMessage={errors.email?.message}
                                autoComplete="email"
                                onChange={onChange}
                                value={value || ""}
                                autoFocus
                            />
                        ) : (
                            <LargeInput
                                type="email"
                                placeholder="Email"
                                errorMessage={errors.email?.message}
                                shouldAutoFocus
                                autoComplete="email"
                                onChange={onChange}
                                value={value}
                            />
                        )
                    )}
                />

                <Button
                    className={styles.primaryButton}
                    isLoading={isLoading}
                    type="submit"
                    disabled={googleLoading || appleLoading}
                >
                    Continue with email
                </Button>
            </form>

            {submitErrorMessage && (
                <div style={{ color: useDarkMode ? "red" : "red", textAlign: "center", marginTop: "16px" }}>
                    {submitErrorMessage}
                </div>
            )}

            <div className={styles.bottomAltActionContainer}>
                Already have an account?
                <span className={styles.altActionSpaceOnLeft} onClick={onLogin}>
                    Log in
                </span>
            </div>

            {useDarkMode && (
                <div className={styles.termsText}>
                    By continuing, you agree to Studio&apos;s{" "}
                    <Link href="/terms" className={styles.termsLink}>Terms</Link>{" "}and{" "}
                    <Link href="/privacy" className={styles.termsLink}>Privacy Policy</Link>
                </div>
            )}
        </div>
    );
}; 